import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo, useState } from 'react';

import { AcceptTermsDocument, TermsAcceptanceDocument } from '@/__generated__/graphql';
import { useT } from '@/core/providers/I18nProvider';
import { logger } from '@/lib/logger';

import {
  TermsAcceptanceContext,
  type TermsAcceptanceContextValue,
} from './terms-acceptance-context';
import { resolveTermsGateStatus } from './terms-gate-status';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * Owns the Terms-of-Service acceptance answer (ADR-0497): it issues the query,
 * performs the acceptance, and publishes the resulting {@link TermsGateStatus}.
 *
 * It ALWAYS renders its children — the blocking is `TermsGate`'s job, deliberately
 * split out. The split exists for latency, and it is the difference between one
 * pre-paint round trip and two:
 *
 *   Providers
 *     TermsAcceptanceProvider   ← query fires here, children render immediately
 *       WorkspaceProvider
 *         WorkspaceHydrator     ← its MyTenants query fires in the SAME tick
 *           TermsGate           ← blocks, reading the answer from this context
 *
 * If this component blocked, `WorkspaceHydrator` would not mount until the
 * acceptance answer arrived, and its query would only then begin — serialising two
 * blocking network calls in front of first paint. Mounting the query above the
 * hydrator and the DECISION below it means both fly concurrently and the wait is
 * `max(a, b)` instead of `a + b`.
 *
 * The query is skipped until authenticated, so a signed-out visitor on a public
 * route issues nothing.
 */

export function TermsAcceptanceProvider({ children }: PropsWithChildren): ReactElement {
  // The LIVE locale, not a fixed prop. The interstitial carries its own language
  // picker (informed consent requires the terms be readable in a language the
  // reader actually reads, and the dashboard's own switcher is behind this gate),
  // so the presented locale can change while the screen is open. Reading it here
  // means the recorded value is whatever was on screen when the button was
  // pressed, which is the only version of this field that is true.
  const { locale: presentedLocale } = useT();
  const { isAuthenticated, isLoading: authLoading, user: auth0User } = useAuth0();
  const client = useApolloClient();

  const { data, error } = useQuery(TermsAcceptanceDocument, {
    skip: !isAuthenticated,
    // The answer changes only when this user accepts, and we write that result
    // into the cache ourselves. `cache-first` therefore means a client-side
    // navigation never re-asks, while a fresh page load (empty in-memory cache —
    // Auth0 is configured `cacheLocation: 'memory'`, so there is no warm start to
    // exploit) still fetches exactly once.
    fetchPolicy: 'cache-first',
    // A GraphQL error at HTTP 200 must surface as `error` rather than throwing
    // into the render, so the gate can fail open instead of white-screening.
    errorPolicy: 'all',
  });

  /**
   * Belt to the cache write's braces. `update` below writes the mutation result
   * into the query's cache entry, which is what makes the interstitial → app
   * transition happen with no refetch and therefore no loading frame. This
   * guarantees that transition even if the cache write ever misses (a renamed
   * document, a normalisation change): without it, such a miss would strand the
   * user on the acceptance screen having already consented, which is the worst
   * available failure.
   *
   * Holds the SUB, not a boolean — see `TermsGateInput.acceptedSub`.
   */
  const [acceptedSub, setAcceptedSub] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptFailed, setAcceptFailed] = useState(false);

  const [runAccept] = useMutation(AcceptTermsDocument);

  const status = useMemo(
    () =>
      resolveTermsGateStatus({
        authLoading,
        isAuthenticated,
        data: data?.termsAcceptance,
        failed: error !== undefined,
        userSub: auth0User?.sub,
        acceptedSub,
      }),
    [authLoading, isAuthenticated, data, error, auth0User?.sub, acceptedSub],
  );

  const accept = useCallback(async (): Promise<void> => {
    // The version is read from the SERVER's answer, never composed on the client:
    // the client has no independent knowledge of which terms are in force, and
    // inventing one would be how a user "accepts" something nobody published.
    const version = data?.termsAcceptance.currentVersion;
    if (version === undefined) {
      setAcceptFailed(true);
      return;
    }

    setAccepting(true);
    setAcceptFailed(false);
    try {
      const result = await runAccept({
        variables: { version, locale: presentedLocale },
        update: (cache, { data: mutationData }) => {
          const accepted = mutationData?.acceptTerms;
          if (accepted === undefined) return;
          // Write the answer the gate reads, so it flips to the app on this same
          // render. A `refetchQueries` here would put a spinner between the click
          // and the dashboard — the one visible flicker this design avoids.
          cache.writeQuery({
            query: TermsAcceptanceDocument,
            data: { termsAcceptance: accepted },
          });
        },
      });

      if (result.data?.acceptTerms.accepted !== true) {
        setAcceptFailed(true);
        return;
      }
      setAcceptedSub(auth0User?.sub ?? null);
    } catch (caught: unknown) {
      // A stale version (the terms were republished while this tab sat open) lands
      // here as a ValidationError. Resetting the store makes the next render read
      // the NEW current version, so the retry accepts what is actually in force
      // rather than re-submitting the version the user's stale page was showing.
      logger.error('[terms] acceptance failed', { error: caught });
      setAcceptFailed(true);
      client
        .refetchQueries({ include: [TermsAcceptanceDocument] })
        .catch((refetchError: unknown) => {
          logger.warn('[terms] refetch after failed acceptance failed', { error: refetchError });
        });
    } finally {
      setAccepting(false);
    }
  }, [auth0User?.sub, client, data, presentedLocale, runAccept]);

  const value = useMemo<TermsAcceptanceContextValue>(
    () => ({ status, accept, accepting, acceptFailed }),
    [status, accept, accepting, acceptFailed],
  );

  return (
    <TermsAcceptanceContext.Provider value={value}>{children}</TermsAcceptanceContext.Provider>
  );
}
